'use client';

import { Search } from 'lucide-react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/ui/button';
import { Link } from '@/components/ui/link';
import {
    NavigationMenu,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
} from '@/components/ui/navigation-menu';
import { useIfMobile } from '@/hooks/use-if-mobile';
import { cn } from '@/lib/utils';
import { Banner } from '@/types/banner';
import { Language } from '@/types/language';
import { SiteSettings } from '@/types/site-settings';
import { Slider } from '@/types/slider';
import { getHeaderHeight } from '@/utils/header';
import { getPageConfig } from '@/utils/routing';

import { ChangeLanguageButton } from '../change-language-button';
import { LanguageSelector } from '../language-selector';

import { HeaderContent } from './header-content';
import { MobileNav } from './mobile-nav';
import { SendRecipeButton } from './send-recipe-button';

const menuItems = [
    { title: 'home', href: '/' },
    { title: 'about', href: '/about' },
    { title: 'recipes', href: '/recipes' },
    { title: 'news', href: '/news' },
    { title: 'events', href: '/events' },
    { title: 'resources', href: '/resources' },
    { title: 'gallery', href: '/gallery' },
];

export const Header = ({
    sliders,
    banner,
    aboutBanner,
    settings,
    languages,
}: {
    sliders: Slider[];
    banner: Banner;
    aboutBanner: Banner;
    settings: SiteSettings;
    languages: Language[];
}) => {
    const pathname = usePathname();
    const t = useTranslations();

    const pathParts = pathname.split('/').filter(Boolean);
    const locale = pathParts[0];

    const pageConfig = getPageConfig(pathname);
    const pageType = pageConfig.type;

    const isMobile = useIfMobile();

    const headerHeight = getHeaderHeight(pageType, isMobile);

    const headerBackground = pageConfig.background || '';

    const isActivePath = (href: string) => {
        if (href === '/') {
            return pathParts.length <= 1 || (pathParts.length === 1 && pathParts[0] === locale);
        }

        const hrefPath = href.startsWith('/') ? href.slice(1) : href;

        const pathWithoutLocale = pathParts.slice(1).join('/');

        return pathWithoutLocale === hrefPath || pathWithoutLocale.startsWith(hrefPath + '/');
    };

    // Check if we have more than 2 languages
    const hasMultipleLanguages = languages.length > 2;

    if (pathname.includes('not-found')) {
        return null;
    }

    return (
        <header className={cn('relative w-full ', headerHeight, headerBackground)}>
            <div className='absolute inset-0 w-full'>
                <HeaderContent
                    banner={banner}
                    aboutBanner={aboutBanner}
                    pageType={pageType}
                    pathname={pathname}
                    sliders={sliders}
                />
            </div>

            <div
                className={cn('relative z-5 w-full', headerHeight, {
                    'bg-gradient-to-b from-black/50 to-transparent': pageType !== 'compact',
                })}
            >
                <div className='mx-auto w-full max-w-[1200px]'>
                    <div className='relative flex items-center p-6 px-4 xl:px-0'>
                        <Link href='/'>
                            <Image
                                src={settings.header_logo}
                                alt='Logo'
                                width={180}
                                height={21.5}
                                priority
                                loading='eager'
                            />
                        </Link>

                        <div className='ml-auto flex items-center gap-4'>
                            <div className='hidden lg:flex'>
                                <SendRecipeButton isActivePath={isActivePath} />
                            </div>
                            <Link href='/search'>
                                <Button
                                    asChild
                                    variant='ghost'
                                    size='icon'
                                    className={cn(
                                        'rounded-full hidden lg:flex hover:bg-white border-2 border-white bg-transparent text-white hover:text-purple justify-center items-center w-[40px] h-[40px] p-2 transition-all duration-300 ease-in-out',
                                    )}
                                >
                                    <Search strokeWidth={3} size={24} />
                                </Button>
                            </Link>
                            <div className='hidden rounded-full lg:flex'>
                                {hasMultipleLanguages ? (
                                    <LanguageSelector languages={languages} />
                                ) : (
                                    languages.length > 1 && <ChangeLanguageButton languages={languages} />
                                )}
                            </div>
                            <MobileNav
                                menuItems={menuItems}
                                isActivePath={isActivePath}
                                hasMultipleLanguages={hasMultipleLanguages}
                                languages={languages}
                            />
                        </div>
                    </div>

                    <div className='relative hidden lg:flex lg:items-center lg:justify-center'>
                        <div className={cn('absolute left-0 top-0 h-[2px] xl:mx-0 mx-4 w-full bg-white')} />
                        <NavigationMenu>
                            <NavigationMenuList className='gap-6'>
                                {menuItems.map(item => {
                                    const isActive = isActivePath(item.href);

                                    return (
                                        <NavigationMenuItem key={item.href}>
                                            <NavigationMenuLink asChild>
                                                <Link
                                                    href={item.href}
                                                    className={cn(
                                                        'group m-0 inline-flex items-center px-3 pt-4 text-sm font-medium uppercase tracking-wide transition-colors',
                                                        {
                                                            'border-t-[2px] border-yellow text-white': isActive,
                                                            'text-white/80 hover:text-white': !isActive,
                                                        },
                                                    )}
                                                >
                                                    {t(item.title) || ''}
                                                </Link>
                                            </NavigationMenuLink>
                                        </NavigationMenuItem>
                                    );
                                })}
                            </NavigationMenuList>
                        </NavigationMenu>
                    </div>
                </div>
            </div>
        </header>
    );
};
